import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthorization } from '../../hooks/useAuthorization';
import type { UserRole } from '../../hooks/useAuthorization';
import { useUserStore } from '../../zustand/UserStore';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermissions?: {
    canAccessSettings?: boolean;
    canAccessAnalysisDashboard?: boolean;
    canAccessMainDashboard?: boolean;
    canAccessUserDashboard?: boolean;
    customCheck?: (role: UserRole) => boolean;
  };
  fallbackRoute?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredPermissions = {},
  fallbackRoute = '/user'
}) => {
  const user = useUserStore((state) => state.user);
  const auth = useAuthorization();

  // If user is not authenticated, redirect to login
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Check permissions
  const hasPermission = () => {
    if (requiredPermissions.canAccessSettings && !auth.canAccessSettings) {
      return false;
    }
    
    if (requiredPermissions.canAccessAnalysisDashboard && !auth.canAccessAnalysisDashboard) {
      return false;
    }
    
    if (requiredPermissions.canAccessMainDashboard && !auth.canAccessMainDashboard) {
      return false;
    }
    
    if (requiredPermissions.canAccessUserDashboard && !auth.canAccessUserDashboard()) {
      return false;
    }
    
    if (requiredPermissions.customCheck && !requiredPermissions.customCheck(user.role as UserRole)) {
      return false;
    }
    
    return true;
  };

  // If user doesn't have permission, redirect to fallback route
  if (!hasPermission()) {
    return <Navigate to={fallbackRoute} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
